import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { paper, fonts, bp } from "../theme";
import { useGameState } from "../GameStateContext";
import { useWebSocket } from "../useWebSocket";
import { rejoinStorage } from "../rejoinStorage";
import { HTTP_URL } from "../config";
import { parseRoute, parseTagUrl } from "../route";
import { isNfcSupported } from "../useNfcScan";
import ScanDeck from "./ScanDeck";

import {
    MsScreen, MsBody, MsFooter, MsBackButton,
    MsDisplay, MsDeck, MsEyebrow, MsRuleRed,
    MsPaper, MsTape, MsBtn, MsTile, MsTileCard,
    MaskHeadline, CodeInput, MsSpinner, MsFieldError,
    Dots, Stack, MsSplitGrid,
} from "../ms";
import { Notice, NoticeStack } from "./Notice";

/* ── Home step: static layout/type overrides ─────────────────────────────── */

const HomeBody = styled(MsBody)`
    justify-content: center;
    gap: 22px;
    padding-bottom: 30px;
`;

const HeroDisplay = styled(MsDisplay)`
    font-size: 56px;
    line-height: 0.95;
`;

const HeroRule = styled(MsRuleRed)`
    margin-top: 18px;
`;

const HeroDeck = styled(MsDeck)`
    margin-top: 14px;
    max-width: 380px;
`;

const HowPaper = styled(MsPaper)`
    padding: 20px 22px;
`;

const HowEyebrow = styled(MsEyebrow)`
    margin-bottom: 10px;
`;

const HowList = styled.ol`
    margin: 0;
    padding: 0 0 0 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HowItem = styled.li`
    font-family: ${fonts.serif};
    font-size: 16px;
    line-height: 1.35;
`;

/* The two host tiles are the same paper chip on two different elements: one a
 * button that opens the scan sheet, the other a static card explaining why you
 * can't host yet. They shared one declaration and an `interactive` flag, swapping
 * tags with `as` — which no longer reaches through MsTile now that it carries a
 * haptic and is a component rather than a styled tag. Two declarations over a
 * shared face costs a line and drops the flag. */
const hostTileFace = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 16px;
    animation: none;
`;

const HostTileButton = styled(MsTile)`
    ${hostTileFace}
    cursor: pointer;
`;

const HostTileCard = styled(MsTileCard)`
    ${hostTileFace}
`;

const HostHint = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 13px;
    color: ${paper.inkSoft};
    line-height: 1.35;
`;

/* ── Code / name steps: static type overrides ────────────────────────────── */

const StepDisplay = styled(MsDisplay)`
    font-size: 40px;
`;

const JoiningDeck = styled(MsDeck)`
    margin-top: 8px;
`;

const RoomCodeMark = styled.strong`
    color: ${paper.ink};
    font-style: normal;
    letter-spacing: 0.04em;
`;

const NamePaper = styled(MsPaper)`
    padding: 0;
`;

const NameEyebrow = styled(MsEyebrow)`
    margin-bottom: 8px;
`;

const NameCount = styled(MsEyebrow)`
    margin-top: 8px;
`;

/* ── Home-screen resume prompt content ──────────────────────────────────── */

const ResumeBody = styled.div`
    line-height: 1.25;
`;

const ResumeTitle = styled.div`
    font-weight: 700;
`;

const ResumeSub = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${paper.inkSoft};
`;

const LoadingWrap = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: ${paper.inkSoft};
`;

const NameSlip = styled.div`
    padding: 20px 22px 22px;
    transform: rotate(-0.8deg);
`;

const HomeHero = styled.div`
    text-align: left;

    @media ${bp.split} {
        h1, .hero-display { font-size: clamp(64px, 7vw, 88px) !important; }
    }
`;

const HomeFooterRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media ${bp.split} {
        flex-direction: row;
        align-items: stretch;

        & > * { flex: 1; }
    }
`;

/* Each step shell: the outgoing step gets pulled out of layout while the
 * incoming step slides in. Children animate in sequence via the stagger
 * declared on the parent's variants. */
const StepShell = styled(motion.div)`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;

    &[data-exiting="true"] {
        position: absolute;
        inset: 0;
        pointer-events: none;
    }
`;

/* Shell + children animate concurrently (no beforeChildren/afterChildren
 * gating) so the new step starts appearing immediately rather than after a
 * dead half-second. The stagger still gives a felt cascade without making the
 * eye wait on it. */
const stepVariants = {
    initial: { opacity: 0, y: 18 },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1] as const,
            staggerChildren: 0.05,
            delayChildren: 0.04,
        },
    },
    exit: {
        opacity: 0,
        y: -20,
        transition: {
            duration: 0.28,
            ease: [0.55, 0, 0.32, 1] as const,
            staggerChildren: 0.03,
            staggerDirection: -1 as const,
        },
    },
};

const itemVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] as const } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: [0.55, 0, 0.32, 1] as const } },
};

const fieldErrorAnim = {
    initial: { opacity: 0, y: -6 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -6 },
    transition: { duration: 0.2 },
};

const NameInput = styled.input`
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 36px;
    color: ${paper.ink};
    letter-spacing: -0.012em;
    padding: 4px 0 8px;
    border-bottom: 2px solid ${paper.ink};
    &::placeholder { color: ${paper.inkMute}; font-style: italic; font-weight: 500; }
`;

/* ── Component ────────────────────────────────────────────── */

type Step = "home" | "join-code" | "join-name" | "create-name";

export default function EnterRoomCode() {
    const [step, setStep] = useState<Step>("home");
    /* While a step transition runs, the entering shell sits at translateY()
     * and its few px of offset briefly spill past 100dvh — on desktop where the
     * window scrolls, that flashes a scrollbar. Clip vertical overflow for the
     * duration of the transition only, so real scrolling is untouched. */
    const [transitioning, setTransitioning] = useState(false);
    const firstStep = useRef(true);
    const [roomCode, setRoomCode] = useState("");
    const [name, setName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    /* Existence check on the code screen (separate from the WS connect loading). */
    const [checking, setChecking] = useState(false);
    const [codeError, setCodeError] = useState<string | null>(null);
    const [showResume, setShowResume] = useState(false);
    const [ownerParams, setOwnerParams] = useState<{ boxId: string; ownerToken: string } | null>(null);
    const [nfcAuto, setNfcAuto] = useState<
        | { status: "idle" }
        | { status: "pending" }
        | { status: "success"; roomCode: string }
        | { status: "invalid"; message: string }
    >({ status: "idle" });
    /* In-app Web NFC scan sheet (Android Chrome/Edge only). On unsupported
     * devices the host tile keeps its passive-tap copy instead. */
    const [showScan, setShowScan] = useState(false);
    const nfcSupported = isNfcSupported();

    const { state, set } = useGameState();
    const { createRoom, connect, sendMessage, rejoin, hasRejoinState } = useWebSocket();

    const { error, connection } = state;
    const isConnected = connection.kind === "connected";

    /* Consume the entry-point URL once (/join/<code>, NFC scan, legacy ?room=,
     * etc.), then clean it back to "/". Unknown paths are redirected home. */
    useEffect(() => {
        const route = parseRoute(window.location.pathname, window.location.search);
        const clean = () => window.history.replaceState({}, "", "/");

        switch (route.kind) {
            case "join":
                setRoomCode(route.code);
                setStep("join-name");
                clean();
                break;
            case "joinPrompt":
                setStep("join-code");
                clean();
                break;
            case "unknown":
                clean(); // stray path → redirect home
                break;
            case "nfc":
                setNfcAuto({ status: "pending" });
                (async () => {
                    try {
                        const result = await createRoom({ boxId: route.boxId, p: route.p, c: route.c });
                        clean();
                        setNfcAuto({ status: "success", roomCode: result.roomCode });
                    } catch (e) {
                        const msg = e instanceof Error && e.message.includes("Authentication")
                            ? "This tag wasn't recognized. Try scanning it again."
                            : "Couldn't validate the tag — check your connection and try again.";
                        setNfcAuto({ status: "invalid", message: msg });
                        clean();
                    }
                })();
                break;
            case "home":
                break;
        }
    }, [createRoom]);

    useEffect(() => {
        if (nfcAuto.status === "success") {
            setRoomCode(nfcAuto.roomCode);
            setStep("join-name");
        }
    }, [nfcAuto]);

    /* Owner-token check — lets you host without re-scanning. */
    useEffect(() => {
        const checkOwnerToken = async () => {
            const token = localStorage.getItem("ownerToken");
            const boxId = localStorage.getItem("ownedBoxId");
            if (!token || !boxId) return;
            try {
                const res = await fetch(`${HTTP_URL}/room/verify-token?boxId=${encodeURIComponent(boxId)}&ownerToken=${encodeURIComponent(token)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.valid) setOwnerParams({ boxId, ownerToken: token });
                    else {
                        localStorage.removeItem("ownerToken");
                        localStorage.removeItem("ownedBoxId");
                    }
                }
            } catch (e) {
                console.error("Failed to verify owner token", e);
            }
        };
        checkOwnerToken();
    }, []);

    // Clear the connect spinner only on a terminal failure. (The previous
    // `!isConnected && isLoading` condition fired the instant loading was set,
    // so the button never actually showed a busy state during connect.) On
    // success the snapshot advances the phase and this screen unmounts.
    useEffect(() => {
        if (error || connection.kind === "failed") setIsLoading(false);
    }, [error, connection.kind]);

    useEffect(() => {
        if (hasRejoinState()) setShowResume(true);
    }, [hasRejoinState]);

    useEffect(() => {
        if (firstStep.current) { firstStep.current = false; return; }
        setTransitioning(true);
        const t = setTimeout(() => setTransitioning(false), 480);
        return () => clearTimeout(t);
    }, [step]);

    const canCreate = !!ownerParams;

    /* Web NFC handoff: a deck scanned in-app yields the same SUN URL the OS would
     * open on a passive tap. Parse it, validate against the backend, and drop the
     * user on the join-name step — identical to the URL entry path. Throws on an
     * unrecognized tag so the scan sheet can show its own retry state. */
    const openScan = () => setShowScan(true);
    const handleTagUrl = useCallback(async (url: string) => {
        const params = parseTagUrl(url, window.location.origin);
        if (!params) throw new Error("Unrecognized tag");
        const result = await createRoom(params);
        setRoomCode(result.roomCode);
        setStep("join-name");
    }, [createRoom]);

    /* Validate the code against the backend before advancing — so a typo is
     * caught here, on the code screen, instead of after the user types a name. */
    const handleFindRoom = async () => {
        const code = roomCode.trim().toUpperCase();
        if (code.length < 4 || checking) return;
        setCodeError(null);
        setChecking(true);
        try {
            const res = await fetch(`${HTTP_URL}/room/${encodeURIComponent(code)}`);
            if (res.status === 404) {
                setCodeError("We couldn't find a room with that code. Double-check it.");
                return;
            }
            if (!res.ok) {
                setCodeError("Something went wrong — please try again.");
                return;
            }
            const data = await res.json().catch(() => null);
            if (data?.full) {
                setCodeError("That room is full.");
                return;
            }
            setRoomCode(code);
            set("error", null); // drop any stale connect error from a prior attempt
            setStep("join-name");
        } catch {
            setCodeError("Couldn't reach the server. Check your connection.");
        } finally {
            setChecking(false);
        }
    };

    /* Auto-submit once all four slots are filled. On mobile this is the whole
     * point: the soft keyboard stays up and the user never has to dismiss it to
     * reach the button (which the keyboard would otherwise cover). The button
     * remains as a visible/fallback affordance. Re-fires after an edit because
     * onChange clears codeError; a standing error keeps the deps stable so it
     * won't loop. */
    useEffect(() => {
        if (step === "join-code" && roomCode.length === 4 && !checking && !codeError) {
            handleFindRoom();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomCode, step]);

    const handleJoinRoom = async () => {
        if (!roomCode.trim() || !name.trim()) return;
        setIsLoading(true);
        set("error", null);
        set("roomId", roomCode);
        try {
            await connect(roomCode);
            sendMessage({ type: "join_room", playerName: name });
        } catch (e) {
            console.error("Join room failed:", e);
            setIsLoading(false);
        }
    };

    const handleCreateRoom = async () => {
        if (!name.trim()) return;
        setIsLoading(true);
        set("error", null);
        try {
            const { roomCode: newRoomCode } = await createRoom(ownerParams || undefined);
            await connect(newRoomCode);
            sendMessage({ type: "join_room", playerName: name });
        } catch (e) {
            console.error("Create room failed:", e);
            setIsLoading(false);
        }
    };

    const handleResume = async () => {
        setIsLoading(true);
        set("error", null);
        try {
            const success = await rejoin();
            if (!success) {
                setIsLoading(false);
                setShowResume(false);
            }
        } catch {
            setIsLoading(false);
            setShowResume(false);
        }
    };

    const handleStartFresh = () => {
        rejoinStorage.clear();
        setShowResume(false);
    };

    if (nfcAuto.status === "pending") {
        return (
            <MsScreen>
                <LoadingWrap>
                    <MsSpinner />
                    <MsDeck>Validating your tag…</MsDeck>
                </LoadingWrap>
            </MsScreen>
        );
    }

    const overlay = (
        <NoticeStack>
            <AnimatePresence>
                {error && step === "home" && (
                    <Notice key="error" tone="error" onDismiss={() => set("error", null)}>
                        {error}
                    </Notice>
                )}
                {nfcAuto.status === "invalid" && !error && (
                    <Notice key="nfc-invalid" tone="error" onDismiss={() => setNfcAuto({ status: "idle" })}>
                        {nfcAuto.message}
                    </Notice>
                )}
                {showResume && step === "home" && !error && (
                    <Notice
                        key="resume"
                        tone="info"
                        actions={[{ label: "Resume", onClick: handleResume, primary: true }]}
                        onDismiss={handleStartFresh}
                    >
                        <ResumeBody>
                            <ResumeTitle>Welcome back</ResumeTitle>
                            <ResumeSub>Pick up your last game</ResumeSub>
                        </ResumeBody>
                    </Notice>
                )}
            </AnimatePresence>
        </NoticeStack>
    );

    /* ── Step shells ───────────────────────────────────────────────
     * Each step is a motion.div under the same AnimatePresence, sharing
     * stepVariants. The exit pulls the old step up & out (popLayout
     * removes it from flow), then the new step rises into place with its
     * children cascading via stagger. The PageBack word field lives at the app
     * root, so it stays put behind every step — the ambient background is
     * unbroken not just across these steps but across all phase transitions. */

    const renderHome = () => (
        <StepShell key="home" variants={stepVariants} initial="initial" animate="animate" exit="exit">
            <HomeBody wide>
                <MsSplitGrid gap={22} align="center" padTop={12}>
                    <motion.div variants={itemVariants}>
                        <HomeHero>
                            <HeroDisplay className="hero-display">
                                <MaskHeadline>Misquote the internet.</MaskHeadline>
                            </HeroDisplay>
                            <HeroRule />
                            <HeroDeck>
                                Build the funniest answer from a Wikipedia article's words.
                            </HeroDeck>
                        </HomeHero>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                        <HowPaper deckle r1="-0.8deg">
                            <MsTape placement="tl" />
                            <HowEyebrow>How a round runs</HowEyebrow>
                            <HowList>
                                <HowItem>One player draws a prompt.</HowItem>
                                <HowItem>Everyone gets a random article.</HowItem>
                                <HowItem>Answer using only its words.</HowItem>
                                <HowItem>Vote on the best one.</HowItem>
                            </HowList>
                        </HowPaper>
                    </motion.div>
                </MsSplitGrid>
            </HomeBody>
            <MsFooter wide>
                <motion.div variants={itemVariants}>
                    <HomeFooterRow>
                        {canCreate ? (
                            <MsBtn variant="red" onClick={() => setStep("create-name")}>Create a room</MsBtn>
                        ) : (
                            <MsBtn variant="red" onClick={() => setStep("join-code")}>Join with a code</MsBtn>
                        )}
                        {canCreate ? (
                            <MsBtn onClick={() => setStep("join-code")}>Join with a code</MsBtn>
                        ) : nfcSupported ? (
                            <HostTileButton type="button" haptic="select" onClick={openScan}>
                                <MsEyebrow tone="red">Want to host?</MsEyebrow>
                                <HostHint>
                                    Tap here, then hold a Misquote deck to your phone to unlock hosting.
                                </HostHint>
                            </HostTileButton>
                        ) : (
                            <HostTileCard>
                                <MsEyebrow tone="red">Want to host?</MsEyebrow>
                                <HostHint>
                                    Scan a Misquote deck with your phone to unlock hosting on this device.
                                </HostHint>
                            </HostTileCard>
                        )}
                    </HomeFooterRow>
                </motion.div>
            </MsFooter>
        </StepShell>
    );

    const renderJoinCode = () => {
        const ready = roomCode.length >= 4;
        return (
            <StepShell key="join-code" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                <MsBody>
                    <Stack gap={26}>
                        <motion.div variants={itemVariants}>
                            <MsBackButton onClick={() => { setCodeError(null); setStep("home"); }} />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <StepDisplay>
                                <MaskHeadline>Enter the room code.</MaskHeadline>
                            </StepDisplay>
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <CodeInput
                                value={roomCode}
                                onChange={(v) => { setRoomCode(v); if (codeError) setCodeError(null); }}
                                length={4}
                                label="Room code"
                            />
                        </motion.div>
                        <AnimatePresence>
                            {codeError && (
                                <MsFieldError key="code-error" {...fieldErrorAnim}>{codeError}</MsFieldError>
                            )}
                        </AnimatePresence>
                    </Stack>
                </MsBody>
                <MsFooter>
                    <motion.div variants={itemVariants}>
                        <MsBtn variant="red" disabled={!ready || checking} onClick={handleFindRoom}>
                            {checking ? <>Finding room <Dots /></> : "Find room"}
                        </MsBtn>
                    </motion.div>
                </MsFooter>
            </StepShell>
        );
    };

    const renderNamePicker = () => {
        const isCreate = step === "create-name";
        const idleLabel = isCreate ? "Create & Join" : "Join room";
        const busyLabel = isCreate
            ? (isConnected ? "Creating room" : "Connecting")
            : (isConnected ? "Joining" : "Connecting");
        const ctaLabel = isLoading ? busyLabel : idleLabel;
        const ready = name.trim().length >= 2;
        return (
            <StepShell key="name" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                <MsBody>
                    <Stack gap={22}>
                        <motion.div variants={itemVariants}>
                            <MsBackButton onClick={() => { if (!isLoading) { set("error", null); setStep(isCreate ? "home" : "join-code"); } }} />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                            <StepDisplay>
                                <MaskHeadline>Pick a name.</MaskHeadline>
                            </StepDisplay>
                            {!isCreate && (
                                <JoiningDeck>
                                    Joining room <RoomCodeMark>{roomCode}</RoomCodeMark>
                                </JoiningDeck>
                            )}
                        </motion.div>

                        <AnimatePresence>
                            {error && (
                                <MsFieldError key="join-error" {...fieldErrorAnim}>{error}</MsFieldError>
                            )}
                        </AnimatePresence>

                        <motion.div variants={itemVariants}>
                            <NamePaper deckle r1="-0.8deg">
                                <MsTape placement="tl" />
                                <NameSlip>
                                    <NameEyebrow>Your name</NameEyebrow>
                                    <NameInput
                                        autoFocus
                                        type="text"
                                        maxLength={16}
                                        value={name}
                                        disabled={isLoading}
                                        // enterKeyHint labels the soft keyboard's action key "Go";
                                        // the onKeyDown below submits on it, so mobile users never
                                        // have to dismiss the keyboard to reach the button.
                                        enterKeyHint="go"
                                        onChange={(e) => setName(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && ready && !isLoading && (isCreate ? handleCreateRoom() : handleJoinRoom())}
                                        placeholder="type here"
                                    />
                                    <NameCount tone="mute">{name.length}/16</NameCount>
                                </NameSlip>
                            </NamePaper>
                        </motion.div>
                    </Stack>
                </MsBody>
                <MsFooter>
                    <motion.div variants={itemVariants}>
                        <MsBtn variant="red" disabled={!ready || isLoading} onClick={() => isCreate ? handleCreateRoom() : handleJoinRoom()}>
                            {ctaLabel} {isLoading && <Dots />}
                        </MsBtn>
                    </motion.div>
                </MsFooter>
            </StepShell>
        );
    };

    return (
        <MsScreen style={transitioning ? { overflowY: "clip" } : undefined}>
            {overlay}
            {/* Steps unmount while the scan sheet is open: the sheet is transparent
                so the one global backdrop shows through, which only works if
                there's nothing rendered beneath it. */}
            {!showScan && (
                <AnimatePresence mode="popLayout" initial={false}>
                    {step === "home" && renderHome()}
                    {step === "join-code" && renderJoinCode()}
                    {(step === "join-name" || step === "create-name") && renderNamePicker()}
                </AnimatePresence>
            )}
            <AnimatePresence>
                {showScan && (
                    <ScanDeck onResolveUrl={handleTagUrl} onClose={() => setShowScan(false)} />
                )}
            </AnimatePresence>
        </MsScreen>
    );
}
